import React from 'react';
import type { Product } from '../../contexts/AuthContext';
import { productCompactDisplayLabel } from '../../shared/utils/productDisplayLabel';

/** Multi-select org products (checkbox list) for invites and roster rows. */
export function OrgProductMultiSelect({
  products,
  selectedIds,
  onChange,
  disabled,
  legend = 'Products',
  hint,
  checkboxTitleByProductId,
  compact = false,
}: {
  products: Product[];
  selectedIds: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  legend?: string;
  hint?: string;
  /** Optional per-product `title` on checkboxes (e.g. roster source tooltip). */
  checkboxTitleByProductId?: Record<string, string>;
  /** Table cells: omit fieldset chrome and legend */
  compact?: boolean;
}) {
  const set = new Set(selectedIds);
  const toggle = (id: string, checked: boolean) => {
    const n = new Set(set);
    if (checked) n.add(id);
    else n.delete(id);
    onChange(Array.from(n));
  };

  if (!products.length) {
    return (
      <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', margin: 0 }}>
        No products.
      </p>
    );
  }

  const innerChecks = (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px 16px',
        alignItems: 'center',
      }}
    >
      {products.map((p) => (
        <label
          key={p.id}
          title={checkboxTitleByProductId?.[p.id]}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: '12px',
            color: 'var(--color-text)',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={set.has(p.id)}
            disabled={disabled}
            onChange={(e) => toggle(p.id, e.target.checked)}
          />
          <span>{productCompactDisplayLabel(p)}</span>
        </label>
      ))}
    </div>
  );

  if (compact) {
    return innerChecks;
  }

  return (
    <fieldset style={{ border: 'none', margin: 0, padding: 0, minWidth: 0 }} aria-label={legend}>
      <legend
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          marginBottom: 6,
          padding: 0,
        }}
      >
        {legend}
      </legend>
      {hint ? (
        <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', margin: '0 0 8px' }}>
          {hint}
        </p>
      ) : null}
      {innerChecks}
    </fieldset>
  );
}
